import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntity } from '../../contexts/EntityContext';
import { api, DebtItem } from '../../lib/api';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount } from '../../lib/format';
import { DebtPaymentModal } from '../../components/cashier/DebtPaymentModal';

// Type badge styles moved to global CSS

export default function CashierDebtPage() {
  const { t } = useTranslation(['common']);
  const { currentEntity } = useEntity();
  const [selectedDebt, setSelectedDebt] = useState<DebtItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['debt', currentEntity?.id],
    queryFn: () => api.debt.list(currentEntity!.id),
    enabled: !!currentEntity,
  });

  if (!currentEntity) return null;

  return (
    <div className="min-h-[calc(100vh-60px)] bg-background">
      <div className="p-5 flex flex-col gap-4">
        <div className="page-header">
          <h1 className="page-title">{t('cashier.debt.title')}</h1>
        </div>

        {data?.totals && data.totals.length > 0 && (
          <div className="bg-surface border border-border rounded-lg px-5 py-4">
            <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wide mb-3">{t('cashier.debt.totals')}</h3>
            <div className="flex gap-8 flex-wrap">
              {data.totals.map((total) => (
                <div key={total.currency} className="flex flex-col gap-1">
                  <span className="text-xs text-foreground-muted font-medium">{total.currency}</span>
                  <span className="text-2xl font-semibold text-error font-mono">{formatAmount(total.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingState message={t('cashier.debt.loadingDebts')} />
        ) : data?.data.length === 0 ? (
          <div className="bg-surface border border-border rounded-lg p-10">
            <EmptyState
              title={t('cashier.debt.noOpenDebts')}
              description={t('cashier.debt.allSettled')}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="table text-[13px]">
              <thead>
                <tr>
                  <th>{t('cashier.debt.tableHeaders.date')}</th>
                  <th>{t('cashier.debt.tableHeaders.type')}</th>
                  <th>{t('cashier.debt.tableHeaders.client')}</th>
                  <th>{t('cashier.debt.tableHeaders.debtAmount')}</th>
                  <th>{t('cashier.debt.tableHeaders.employee')}</th>
                  <th className="text-right whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody>
                {data?.data.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate((item as any).transactionDate || item.createdAt)}</td>
                    <td>
                      <span className={`badge badge-${item.type}`}>{item.type}</span>
                    </td>
                    <td>{(item as any).clientId || '-'}</td>
                    <td className="font-mono text-error font-semibold">{item.debtAmount}</td>
                    <td>{item.employeeName}</td>
                    <td className="text-right whitespace-nowrap">
                      <div className="flex gap-2 justify-end">
                        <button
                          className="btn btn-primary px-3 py-1.5 text-xs"
                          onClick={() => setSelectedDebt(item)}
                        >
                          {t('cashier.debt.addPayment')}
                        </button>
                        <Link to={`/transactions/${item.id}`} className="btn btn-secondary px-3 py-1.5 text-xs">
                          {t('cashier.debt.view')}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedDebt && (
          <DebtPaymentModal
            transactionId={selectedDebt.id}
            entityId={currentEntity.id}
            onClose={() => setSelectedDebt(null)}
          />
        )}
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
